import React, { useEffect } from 'react'
import {
  Backdrop,
  Button,
  ButtonProps,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Theme,
  Box,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { SxProps } from '@mui/system'
import CloseIcon from '@mui/icons-material/Close'

import { VARS } from 'src/styles/vars'
import { useIsMobile } from 'src/common/hooks/useIsMobile'

import { lockBodyScroll, unlockBodyScroll } from './bodyScrollLock'


export interface CommonDialogProps {
  isOpen: boolean
  onClose: () => void
  onAdmit?: () => void
  title?: string
  dialogProps?: Omit<DialogProps, 'open' | 'onClose'>
  contentClassName?: string
  contentSx?: SxProps<Theme>
  cancelBtnProps?: Omit<ButtonProps, 'onClick'>
  admitBtnProps?: Omit<ButtonProps, 'onClick'>
  isLoading?: boolean
  /* Разрешить закрыть окно, пока идёт сохранение. По умолчанию оно заперто, чтобы владелец не ушёл
     из формы посреди записи; но если запрос завис, запертое окно не закрыть даже крестиком. */
  closableWhileLoading?: boolean
  isContentLoading?: boolean
  muiContentSx?: SxProps<Theme>
  customDialogActions?: React.ReactNode
  dialogActionsSx?: SxProps<Theme>
  disableMobileFullscreen?: boolean
  /* Не затемнять содержимое на время сохранения: индикатор остаётся только в кнопке.
     Для длинных форм затемнение читается как «страница перезагружается» (KNM-136). */
  disableLoadingBackdrop?: boolean
  children: React.ReactNode
}

export const CommonDialog: React.FC<CommonDialogProps> = ({
  isOpen,
  title,
  dialogProps,
  onClose,
  onAdmit,
  cancelBtnProps,
  admitBtnProps,
  isLoading,
  closableWhileLoading,
  children,
  isContentLoading,
  contentSx,
  muiContentSx,
  customDialogActions,
  dialogActionsSx,
  disableMobileFullscreen,
  disableLoadingBackdrop,
}) => {
  const isMobileScreen = useIsMobile()
  const isMobile = isMobileScreen && !disableMobileFullscreen

  const handleClose = () => {
    if (isLoading && !closableWhileLoading) {
      return
    }

    onClose()
  }

  /* Скролл фона блокируем, только пока диалог открыт: счётчик внутри выдерживает
     вложенные модалки, а на десктопе блокировку целиком делает MUI (см. bodyScrollLock).
     Смотрим на ширину экрана, а не на isMobile: окно без полноэкранного режима
     (disableMobileFullscreen) — всё равно окно, фон под ним на телефоне тянуться не должен. */
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    lockBodyScroll(isMobileScreen)

    return () => unlockBodyScroll()
  }, [isOpen, isMobileScreen])

  // контент модалки мигает при закрытии без этой проверки
  if (!isOpen) {
    return null
  }

  let sx: SxProps<Theme> = {
    marginTop: isMobile ? 3 : 2,
    minWidth: isMobile ? 0 : 240,
    marginBottom: 2,
  }

  if (contentSx) {
    sx = {
      ...sx,
      ...contentSx,
    }
  }

  let muiSx: SxProps<Theme> = {
    paddingBottom: 0,
    paddingLeft: isMobile ? '16px' : '24px',
    paddingRight: isMobile ? '16px' : '24px',
    overflowX: 'hidden',
  }
  
  if (muiContentSx) {
    muiSx = {
      ...muiSx,
      ...muiContentSx,
    }
  }

  return (
    <Dialog
      open
      onClose={handleClose}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      fullScreen={isMobile}
      {...dialogProps}
    >
      <DialogTitle
        sx={
          {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'center' : 'flex-start',
            paddingY: 0,
            minHeight: isMobile ? {
              xs: '56px',
              sm: '64px',
            } : '64px',
            paddingX: isMobile ? '16px' : undefined,
            color: isMobile ? 'white' : undefined,
            backgroundColor: isMobile ? VARS.BLUE_MAIN : undefined,
          }
        }
      >
        <Box
          sx={
            {
              order: isMobile ? 1 : 0,
              marginRight: isMobile ? 'auto' : 0,
              fontWeight: isMobile ? '500' : '400',
              fontSize: isMobile ? '20px' : '24px',
              lineHeight: isMobile ? '160%' : '132%',
              paddingTop: isMobile ? 0 : '15px',
              paddingBottom: isMobile ? 0 : '15px',
            }
          }
        >
          { title }
        </Box>
        <IconButton
          edge='start'
          size='large'
          color='inherit'
          onClick={handleClose}
          aria-label='close'
          sx={
            {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isLoading && !closableWhileLoading ? 'auto' : 'pointer',
              color: !isMobile ? VARS.TEXT_SECONDARY : 'inherit',
              marginLeft: isMobile ? '-16px' : 2,
              marginRight: isMobile ? '16px' : '-16px',
              marginTop: isMobile ? 0 : '7px',
            }
          }
        >
          <CloseIcon />
        </IconButton>

        {
          isMobile && onAdmit && (
            <Button
              variant='contained'
              color='secondary'
              size='small'
              onClick={!isLoading ? onAdmit : undefined}
              {...admitBtnProps}
              sx={
                {
                  backgroundColor: '#fff',
                  marginLeft: 1,
                  order: 3,
                  ...admitBtnProps?.sx,
                }
              }
            >
              { admitBtnProps?.children ?? 'Сохранить' }
            </Button>
          )
        }
      </DialogTitle>

      <DialogContent
        sx={muiSx}
      >
        {
          isContentLoading ? (
            <Box
              sx={
                {
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '60px',
                }
              }
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={sx}
            >
              { children }
            </Box>
          )
        }

      </DialogContent>
      {
        !isMobile && (
          <DialogActions
            sx={
              {
                padding: '16px 24px',
                ...dialogActionsSx,
              }
            }
          >
            { customDialogActions }

            <Button
              variant='contained'
              color='secondary'
              onClick={handleClose}
              disabled={isLoading}
              {...cancelBtnProps}
            >
              { cancelBtnProps?.children ?? 'Закрыть' }
            </Button>

            {
              onAdmit && (
                <LoadingButton
                  variant='contained'
                  onClick={onAdmit}
                  loading={isLoading}
                  {...admitBtnProps}
                  sx={
                    {
                      marginLeft: 1,
                      ...admitBtnProps?.sx,
                    }
                  }
                >
                  { admitBtnProps?.children ?? 'Сохранить' }
                </LoadingButton>
              )
            }
          </DialogActions>
        )
      }

      {
        isLoading && !disableLoadingBackdrop && (
          <Backdrop
            sx={
              {
                color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1,
              }
            }
            open={isLoading}
            /* Подложка перекрывает крестик, поэтому клик по ней сам закрывает окно: иначе из
               зависшего сохранения не выйти. Escape работает через onClose диалога. */
            onClick={closableWhileLoading ? handleClose : undefined}
          >
            <CircularProgress color='inherit' />
          </Backdrop>
        )
      }
    </Dialog>

  )
}
